import React from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent } from './Shared';
import { ProductVideo } from './ProductVideo';
import { featuredTestimonials } from '../data/testimonials';
import { ShieldCheck, Lock, BadgeCheckIcon, Star, X, ArrowRightIcon, FileTextIcon, ZapIcon, LayersIcon, TrophyIcon } from 'lucide-react';
export const HERO_MOCKUP = 'https://cdn.magicpatterns.com/uploads/bGFnoJ26mLpq5ytiyGic7n/Mockup_principal.png';
const PRODUCT_VIDEO = 'https://cdn.magicpatterns.com/uploads/nqhnHuyYbSeddpSiQj3xwK/video%20libros.mp4';
export const Hero = () => {
  const chips = [{
    icon: FileTextIcon,
    label: '100% pdf'
  }, {
    icon: ZapIcon,
    label: 'Toegang in 5 min'
  }, {
    icon: LayersIcon,
    label: '365 sessies'
  }];
  return <section className="relative overflow-hidden border-b border-white/5 bg-slate-950" data-id="element-291" data-mp="dyhigo8gz05h">
      {/* Subtle top light — solid, not a gradient */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-sky-400/40" aria-hidden="true" data-id="element-292" data-mp="1pey0mpberuw1" />
      {/* minmax(0,1fr) keeps the mobile column exactly the screen width, even
          when content is wider — nothing gets pushed right. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] items-center justify-items-center gap-3 px-5 pb-10 pt-5 sm:gap-6 sm:pt-8 md:px-8 md:pb-20 md:pt-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:justify-items-stretch lg:gap-x-12 lg:gap-y-8" data-id="element-293" data-mp="1c9ejlhwrtpc4">
        {/* Headline — fixed line breaks on mobile, fluid size so every line fits */}
        <FadeIn className="flex w-full min-w-0 flex-col items-center text-center lg:col-start-1 lg:row-start-1 lg:items-start lg:self-end lg:text-left" data-id="element-294" data-mp="tjy6lv3wg4et">
          <p className="mb-2.5 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-sky-400 sm:mb-4 sm:text-xs" data-id="element-295" data-mp="kkbddwlbq4wu">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" aria-hidden="true" data-id="element-296" data-mp="fkf5hm9i9tc0" />
            Functionele Training 365™
          </p>
          {/* Italic glyphs lean right; a tiny right padding re-centers them optically */}
          <h1 className="w-full break-words pr-[0.06em] font-display text-[clamp(30px,9.6vw,42px)] font-black uppercase italic leading-[0.88] tracking-tight text-white sm:text-5xl sm:leading-[0.92] md:text-6xl lg:pr-0 lg:text-[64px]" data-id="element-297" data-mp="5xclk0ik18hl">
            <span className="block sm:inline" data-id="element-298" data-mp="3s5lyuyf71lr4">Plan je hele</span>{' '}
            <span className="block sm:inline" data-id="element-299" data-mp="jp11yfzu6j4d">jaar aan</span>{' '}
            <span className="block sm:inline" data-id="element-300" data-mp="jqu7tt2mkzbb"><Accent data-id="element-301" data-mp="onkmqd2p1ov5">functionele</Accent></span>{' '}
            <span className="block sm:inline" data-id="element-302" data-mp="on2hws1yu2bm">lessen.</span>
            <span className="mt-1 block text-slate-400" data-id="element-303" data-mp="9zpt8ugv3gh0">In 5 minuten.</span>
          </h1>
          <p className="mx-auto mt-3 max-w-[320px] text-[15px] leading-snug text-slate-300 sm:mt-5 sm:max-w-md sm:text-base sm:leading-relaxed md:text-lg lg:mx-0" data-id="element-304" data-mp="exixhhylu0en">
            365 kant-en-klare sessies — of je nu een ervaren coach bent of net
            begint.
          </p>
        </FadeIn>

        {/* Mockup */}
        <FadeIn delay={0.1} className="flex w-full min-w-0 justify-center lg:col-start-2 lg:row-span-2 lg:row-start-1" data-id="element-305" data-mp="2s3pam7iab49f">
          <a href="#pricing" className="group block w-full" aria-label="Bekijk het aanbod" data-id="element-306" data-mp="2n54kx20ns2z8">
            <img src={HERO_MOCKUP} alt="Functionele Training 365 — de 6 delen, het bonuspakket en digitale toegang op tablet, computer en telefoon" width={1536} height={1024} loading="eager" decoding="async"
          // @ts-ignore fetchpriority is valid HTML
          fetchpriority="high" className="mx-auto block h-auto w-full max-w-[400px] object-contain sm:max-w-[560px] transition-transform duration-200 ease-out group-hover:-translate-y-1 lg:max-w-none" data-id="element-307" data-mp="31t8wlm1ane8i" />
          </a>
        </FadeIn>

        {/* CTA */}
        <FadeIn delay={0.15} className="flex w-full min-w-0 max-w-[420px] flex-col items-center sm:max-w-none lg:col-start-1 lg:row-start-2 lg:items-start lg:self-start" data-id="element-308" data-mp="3tnszhbtlbn5r">
          <ul className="mb-4 flex w-full flex-wrap items-center justify-center gap-1.5 sm:mb-5 sm:gap-2 lg:justify-start" data-id="element-309" data-mp="oqhwx2scsrjd">
            {chips.map(({
            icon: Icon,
            label
          }) => <li key={label} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 sm:gap-1.5 sm:px-3 sm:py-1.5 sm:text-xs md:text-sm" data-id="element-310" data-mp="xok4y2ai8kg5">
                <Icon size={14} className="text-sky-400" aria-hidden="true" data-id="element-311" data-mp="1bol5fgmuxpv3" />
                {label}
              </li>)}
          </ul>
          <CTAButton href="#pricing" className="w-full sm:w-auto" data-id="element-312" data-mp="1gi66jhjqyzyi">
            Ja, ik wil toegang
            <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-313" data-mp="1lhaek4uxqrn9" />
          </CTAButton>
          <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-[11px] text-slate-400 sm:mt-4 sm:gap-x-4 sm:gap-y-1.5 sm:text-xs lg:justify-start" data-id="element-314" data-mp="1vcke8lxv2mxv">
            <span className="flex items-center gap-1.5" data-id="element-315" data-mp="3aw1t9cpylyc2">
              <ShieldCheck size={14} className="text-emerald-400" aria-hidden="true" data-id="element-316" data-mp="3ayo0fb8xubtr" /> 7 dagen garantie
            </span>
            <span className="flex items-center gap-1.5" data-id="element-317" data-mp="30yngpo75ptp1">
              <Lock size={14} aria-hidden="true" data-id="element-318" data-mp="3108b9dth1cmw" /> Veilig afrekenen
            </span>
            <span className="flex items-center gap-1.5" data-id="element-319" data-mp="2fyq9k9skxu2">
              <BadgeCheckIcon size={14} className="text-sky-400" aria-hidden="true" data-id="element-320" data-mp="3tm9l2yo7l2cy" /> Geen verborgen kosten
            </span>
          </div>
        </FadeIn>
      </div>
    </section>;
};
export const SocialProof = () => {
  const stats = [{
    value: '+500',
    label: 'coaches vertrouwen erop'
  }, {
    value: '6',
    label: 'kant-en-klare delen'
  }, {
    value: '5',
    label: 'bonussen inbegrepen'
  }, {
    value: '4,9/5',
    label: 'gemiddelde score',
    star: true
  }];
  return <div className="border-b border-white/5 bg-slate-900/40 px-5 py-8 md:px-8" data-id="element-321" data-mp="2ao4vvg5bkw5r">
      <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-y-6 md:grid-cols-4" data-id="element-322" data-mp="2aq7v8qsd41n5">
        {stats.map((s, i) => <FadeIn key={s.label} delay={Math.min(i * 0.05, 0.2)} className={`px-2 text-center ${i % 2 === 1 ? 'border-l border-white/10' : ''} ${i > 0 ? 'md:border-l md:border-white/10' : ''}`} data-id="element-323" data-mp="20yblgigwyn33" data-mpi={i}>
            <dt className="sr-only" data-id="element-324" data-mp="25v2154ot3gnz">{s.label}</dt>
            <dd className="font-display text-4xl font-black italic leading-none tracking-tight text-white md:text-5xl" data-id="element-325" data-mp="1rwkjnophv0ei">
              {s.value}
            </dd>
            <dd className="mt-2 flex items-center justify-center gap-1 text-sm text-slate-400" data-id="element-326" data-mp="1hzpj709nupxl">
              {s.label}
              {s.star && <Star size={12} className="fill-amber-400 text-amber-400" aria-hidden="true" data-id="element-327" data-mp="1mzlod2u3gneq" />}
            </dd>
          </FadeIn>)}
      </dl>
    </div>;
};
const Stars = ({
  size = 12
}: {
  size?: number;
}) => <div className="flex text-amber-400" role="img" aria-label="5 van de 5 sterren" data-id="element-328" data-mp="3hc1qpsjrrheb">
    {[...Array(5)].map((_, j) => <Star key={j} size={size} className="fill-current" aria-hidden="true" data-id="element-329" data-mp="15n3ytzpeeiy9" data-mpi={j} />)}
  </div>;
export const Testimonials1 = () => <Section className="bg-slate-950 !py-12 md:!py-16" data-id="element-330" data-mp="1ferktl36r7sc">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-id="element-331" data-mp="1fgce77drkkom">
      {featuredTestimonials.map((t, i) => <FadeIn key={t.author} delay={Math.min(i * 0.05, 0.2)} data-id="element-332" data-mp="1amzzpq2stu9l" data-mpi={i}>
          <figure className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6" data-id="element-333" data-mp="1oeofc3c9b0vb">
            <Stars data-id="element-334" data-mp="1jg3cdw5me336" />
            <blockquote className="mt-3 text-[15px] leading-relaxed text-slate-200" data-id="element-335" data-mp="1jipjxvsigile">
              “{t.text}”
            </blockquote>
            <figcaption className="mt-auto flex items-center gap-3 pt-5" data-id="element-336" data-mp="1tf2eevvvr0h7">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.bg} text-xs font-bold text-white`} data-id="element-337" data-mp="1tcybag5t25hz">
                {t.initials}
              </div>
              <div className="min-w-0" data-id="element-338" data-mp="3v80cp78fel9j">
                <p className="text-sm font-semibold leading-tight text-white" data-id="element-339" data-mp="39di4sid07lfi">{t.author}</p>
                <p className="text-xs text-slate-500" data-id="element-340" data-mp="3ebte8rzdztx7">{t.role}</p>
              </div>
            </figcaption>
          </figure>
        </FadeIn>)}
    </div>
  </Section>;
export { Stars };
export const Problem = () => {
  const painPoints = ['Je komt de sportschool binnen zonder precies te weten welke les je vandaag geeft', 'Je bent uren kwijt aan het zoeken naar oefeningen die bij je groep passen', 'Je herhaalt steeds dezelfde circuits omdat je geen sessiebibliotheek hebt', 'Er komt een nieuw lid binnen en je weet niet hoe je ter plekke moet aanpassen', 'Je wilt een jaarprogramma, maar je hebt geen tijd om het op te bouwen', 'Je groep stagneert telkens als je ze echte progressie probeert te geven', 'Weekenden ideeën zoeken. Maandagen improviseren. Steeds weer hetzelfde.'];
  return <Section className="bg-slate-900/40" data-id="element-341" data-mp="jmmysbyagkfe">
      <FadeIn data-id="element-342" data-mp="jpivjwyeh329">
        <SectionTitle data-id="element-343" data-mp="245p8v22frk11">
          Geef je <Accent data-id="element-344" data-mp="244nna3nsnf52">functionele training</Accent>?
          <br className="hidden md:block" data-id="element-345" data-mp="247jlzu7gu7uk" /> Dan komt dit je bekend voor…
        </SectionTitle>
      </FadeIn>

      <ul className="mx-auto mb-16 grid max-w-4xl grid-cols-1 gap-x-8 gap-y-1 md:mb-24 md:grid-cols-2" data-id="element-346" data-mp="1vdf07p76vmlw">
        {painPoints.map((point, i) => <li key={point} className={`flex items-start gap-3 border-b border-white/5 py-3.5 ${i === painPoints.length - 1 ? 'md:col-span-2 md:mx-auto md:max-w-md md:border-b-0' : ''}`} data-id="element-347" data-mp="1qd91jp2i4zub" data-mpi={i}>
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500/15" data-id="element-348" data-mp="1gfuxkoxrm3iw">
              <X size={12} className="text-red-400" strokeWidth={3} aria-hidden="true" data-id="element-349" data-mp="1cfc6wy7lz3ia" />
            </span>
            <p className="text-[15px] leading-normal text-slate-300 md:text-base" data-id="element-350" data-mp="17h0uqgtucgww">{point}</p>
          </li>)}
      </ul>

      <FadeIn className="mx-auto mb-12 grid max-w-5xl items-center gap-6 md:mb-16 md:grid-cols-2 md:gap-12" data-id="element-351" data-mp="36q7q0ugf60zb">
        <h3 className="text-center font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-left md:text-6xl" data-id="element-352" data-mp="ibmxwuc6625h">
          Nog steeds je trainingen aan het <Accent data-id="element-353" data-mp="dewi884a18kl">improviseren</Accent>?
        </h3>
        <div className="space-y-4 text-center text-base leading-relaxed text-slate-300 md:text-left md:text-lg" data-id="element-354" data-mp="rb9yxf0zl5y0">
          <p data-id="element-355" data-mp="meji2phcy6bj">
            Je hebt voor dit vak gekozen omdat je houdt van functionele
            training en je groep graag ziet groeien.
          </p>
          <p data-id="element-356" data-mp="3qrq4sxvgjmsr">
            Of je nu een ervaren coach bent of net begint, het probleem is
            hetzelfde: je bent meer tijd kwijt aan <strong className="font-semibold text-white" data-id="element-357" data-mp="8eijxhsh9czr">ZOEKEN</strong> naar
            wat je gaat geven dan aan echt <strong className="font-semibold text-white" data-id="element-358" data-mp="3lyunnkfgtf4">LESGEVEN</strong>. En terwijl
            jij je zondagen opoffert aan het bouwen van sessies, lopen je
            concurrenten steeds verder uit.
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mx-auto max-w-3xl" data-id="element-359" data-mp="371mpd2v67cm0">
        <div className="rounded-3xl border border-sky-400/40 bg-blue-600/10 px-6 py-8 text-center md:px-10 md:py-10" data-id="element-360" data-mp="1mkdjzmi2sn5q">
          <p className="font-display text-2xl font-black uppercase italic leading-[1.02] tracking-tight text-white md:text-4xl" data-id="element-361" data-mp="1rdpvcuxquxgj">
            Wat als dit de <Accent data-id="element-362" data-mp="1ijlbivh12mai">laatste les</Accent>
            <br data-id="element-363" data-mp="1ikmnr39s5ett" /> was die je zo voorbereidt?
          </p>
        </div>
      </FadeIn>

      <FadeIn className="mt-20 text-center md:mt-28" data-id="element-364" data-mp="2695sj8tmf9h5">
        <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-950" data-id="element-365" data-mp="267l29vecacp3">
          <TrophyIcon size={14} aria-hidden="true" data-id="element-366" data-mp="1wagang1q5p0w" />
          Het #1 systeem voor functionele trainers
        </p>
        <h2 className="font-display text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-6xl" data-id="element-367" data-mp="218roe1v54joe">
          Functionele Training <Accent data-id="element-368" data-mp="pvp52z1npg0j">365™</Accent>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-369" data-mp="pwqru0s1bqy3">
          6 delen + 5 bonussen — 365 kant-en-klare sessies · Een heel jaar ·
          Alles geregeld.
        </p>

        <div className="relative mx-auto mt-10 max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/50" data-id="element-370" data-mp="1s9apqudp0ea">
          <ProductVideo src={PRODUCT_VIDEO} label="Preview van de werkboeken van Functionele Training 365" className="block w-full object-cover" data-id="element-371" data-mp="3o7u3o1zkvk78" />
        </div>
      </FadeIn>
    </Section>;
};